export interface Merchant {
  name: string
  id: string
  document_id: string
}

export type MerchantIsAbleToTransactParams = {
  merchant_document_number: string
}

export type MerchantIsAbleToTransactResponse = {
  company_name: string
  is_able_to_transact: boolean
}

export type MerchantProfile = {
  id: string
  available_limit_cents: number | null
  company_name: string
  document_number: string
  is_able_to_transact: boolean
  status: MerchantStatus
  total_limit_cents: number | null
  users: Array<{ email: string }>
}

export type MerchantProfileId = {
  id: string
  legal_name?: string
  document_number?: string
  fantasy_name?: string
}

export type MerchantContact = {
  email: string
  name: string
  phone: string
  user_id: string
}

export type MerchantResponse = Omit<Merchant, 'document_id'> & {
  document_number: string
  status?: MerchantStatus | undefined
  created_at?: Date | undefined
}

export type GetMerchantsResponseData = {
  merchants: Array<MerchantResponse>
}

export enum EmptyBillListStatus {
  Analysis,
  Waiting,
  Available,
  Blocked,
  Unknown,
}

export type MerchantStatusInfo = MerchantResponse

export enum MerchantStatus {
  PendingIdentification = 'kyc_pending',
  WaitingLimitAnalisys = 'limit_pending',
  BlockedLimit = 'limit_blocked',
  Allowed = 'able_to_transact',
  RejectedTerms = 'term_rejected',
  RequestedTerms = 'term_requested',
  UnavailableLimit = 'limit_unavailable',
  RejectedLimit = 'limit_rejected',
}

export type MerchantRefusedReason = {
  usecase: string
  title: string
  description: string
}

export type MerchantApprovedReason = {
  flag_tino_relationship: string
  company_age_cluster: string
  available_limit_cluster: string
  score_pj_cluster: string
}

export type MerchantShareholder = {
  name?: string
  cpf?: string
  entry_date?: string
  function?: string
  ownership_percentage?: number
  negative_reports?: number
  last_negative_report_date?: string | null
}

export type MerchantShareholderParticipation = {
  company_name?: string
  company_document_id?: string
  company_status?: string
  ownership_percentage?: number
  entry_date?: string
}

export type MerchantShareholderExternalParticipation = {
  partner_name?: string
  cpf?: string
  participation?: MerchantShareholderParticipation[]
}

export type MerchantCommercialReference = {
  supplier_document_number?: string
  supplier_name?: string
  amount_cents?: number
  number_of_boletos?: number
}

export type RatingScore = 'A' | 'B' | 'B-' | 'C' | 'C-' | 'D'

export enum MerchantLimitStatus {
  Unknown = 'unknown',
  Approved = 'approved',
  Unavailable = 'unavailable',
  Blocked = 'blocked',
  StrongBlocked = 'strong-blocked',
  Rejected = 'rejected',
  Pending = 'not_evaluated',
}

export enum MerchantOptInStatus {
  NonRequested = 'NON_REQUESTED',
  Pending = 'PENDING',
  Accepted = 'ACCEPTED',
  Rejected = 'REJECTED',
}

export type MerchantTrustedContact = {
  contact: string
  type: 'email' | 'phone'
}

export type MerchantSocialMedia = {
  link: string
  name: string
  app: 'instagram' | 'ifood'
}

export enum MerchantChangeType {
  DocumentNumber = 'document_number',
  TradeName = 'trade_name',
  RegistrationStatusChangeDate = 'registration_status_change_date',
  HeadquartersBranch = 'headquarters_branch',
  RegistrationStatus = 'registration_status',
  PrimaryCnae = 'primary_cnae',
  SecondaryCnae = 'secondary_cnae',
  Address = 'address',
  ZipCode = 'zip_code',
  State = 'state',
  City = 'city',
  Country = 'country',
  Phone1 = 'phone_1',
  Phone2 = 'phone_2',
  Email = 'email',
  SpecialStatus = 'special_status',
  SimplesOption = 'simples_option',
  MeiOption = 'mei_option',
  Partners = 'partners',
}

export type MerchantChange = {
  month: number
  year: number
  title: string
  change: MerchantChangeType
  newData: string
  oldData: string
}

export type MerchantChanges = {
  documentNumber: string
  totalChanges: number
  changes: MerchantChange[]
}

export type MonthlyBreakdown = {
  month_year: string
  count: number
}

export type RecentInquiry = {
  date: string
  company_name: string
  company_document_number: string
}

export type Inquiries = {
  monthly_breakdown: MonthlyBreakdown[]
  recent_inquiries: RecentInquiry[]
  period_end: string
  period_start: string
  total_count: number
}

export type MerchantProtestOccurrence = {
  date: string
  notary: string
  city: string
  state: string
  value: number
}

export type NegativeData = {
  occurred_at: string
  amount: number
}

export type NegativeDatas = {
  restrictions_count: number
  total_debt: number
  medium_debt: number
  last_restriction: NegativeData
}

export type LossVsLimitChartAxis = {
  probability_loss: number
  limit: number
}

export type LossVsLimitChartProps = {
  matrix: LossVsLimitChartAxis[]
  tino_limit: LossVsLimitChartAxis
}

export type MerchantRatings = {
  id?: string
  hasOptIn?: boolean
  trusted_contacts?: MerchantTrustedContact[]
  available_limit_cents?: number | null
  bad_checks?: number
  bankruptcy_recovery?: number
  civil_actions?: number
  commercial_references?: MerchantCommercialReference[]
  company_address?: string
  company_document_number?: string
  document_number?: string
  company_status?: string
  company_trade_name?: string
  credit_granted_limit_cents?: number | null
  dda_opt_in_date?: string | null
  external_consultations?: number | null
  external_credit_usage?: number | null
  first_protest_date?: string | null
  negative_data?: NegativeDatas | null
  inquiries?: Inquiries
  is_member_of_simples_nacional?: boolean | null
  granted_limit_cents?: number | null
  has_external_credit?: boolean | null
  informed_return_checks?: number
  judicial_protests?: number
  latest_protest_amount_cents?: number | null
  latest_protest_date?: string | null
  latest_protests_occurrences?: MerchantProtestOccurrence[]
  legal_nature?: string
  legal_person_status?: string
  limit_status?: MerchantLimitStatus
  loss_probabilities?: LossVsLimitChartProps
  opt_in_status?: MerchantOptInStatus
  main_cnae?: string
  max_due_days?: number[]
  message?: string
  name?: string
  number_of_partners?: number
  rating?: RatingScore
  receita_federal_opening_date?: string
  receita_federal_status?: string
  refuse_reason?: MerchantRefusedReason
  approved_reason?: MerchantApprovedReason
  score?: number
  share_capital_cents?: number
  shareholders?: MerchantShareholder[]
  shareholders_external_participation?: MerchantShareholderExternalParticipation[]
  state_registration?: string | null
  stopped_checks?: number
  total_protests_amount_cents?: number | null
}

export type Review = {
  author_name: string
  author_photo_url: string
  rating: number
  text: string
  time: string
}

export type Places = {
  formatted_address?: string
  place_id?: string
  user_rating_count?: number
  name?: string
  photos?: string[]
  primary_type_display_name?: string
  rating?: number
}
